import { motion } from 'framer-motion'
import { format, isBefore, startOfToday, isSameDay, isSameMonth } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBookingCalendar, formatTime } from '@/hooks/useBookingCalendar'
import type { ArtistAvailability } from '@/types/booking'
import { useEffect } from 'react'

interface DirectBookingCalendarCardProps {
  availability: ArtistAvailability | null
  earliestDate: Date
  estimatedTime: number | 'unknown'
  onDateSelect: (date: Date | null) => void
  onTimeSlotSelect: (slot: { start: number; end: number } | null) => void
  selectedDate: Date | null
  selectedTimeSlot: { start: number; end: number } | null
  isLoading?: boolean
}

export function DirectBookingCalendarCard({
  availability,
  earliestDate,
  estimatedTime,
  onDateSelect,
  onTimeSlotSelect,
  selectedDate: externalSelectedDate,
  selectedTimeSlot: externalSelectedTimeSlot,
  isLoading = false
}: DirectBookingCalendarCardProps) {
  const {
    currentMonth,
    slideDirection,
    handlePrevMonth,
    handleNextMonth,
    handleDayClick,
    handleTimeSlotClick,
    getAvailableTimeSlots,
    get30MinuteIntervals,
    hasAvailableSlots,
    daysInMonth,
    paddingDays,
    dayNames
  } = useBookingCalendar(availability, earliestDate, estimatedTime)

  // Use external selected date for displaying time slots
  const selectedDate = externalSelectedDate
  const selectedTimeSlot = externalSelectedTimeSlot

  // Auto-navigate calendar to the selected date's month if different
  useEffect(() => {
    if (selectedDate && !isSameMonth(selectedDate, currentMonth)) {
      // Navigate to the month containing the selected date
      const monthDiff = (selectedDate.getFullYear() - currentMonth.getFullYear()) * 12 + 
                       (selectedDate.getMonth() - currentMonth.getMonth())
      
      if (monthDiff > 0) {
        // Need to go forward
        for (let i = 0; i < monthDiff; i++) {
          handleNextMonth()
        }
      } else if (monthDiff < 0) {
        // Need to go backward
        for (let i = 0; i < -monthDiff; i++) {
          handlePrevMonth()
        }
      }
    }
  }, [selectedDate, currentMonth, handleNextMonth, handlePrevMonth])

  const handleDateClick = (date: Date) => {
    handleDayClick(date)
    onDateSelect(isSameDay(date, selectedDate || new Date('1970-01-01')) ? null : date)
  }

  const handleSlotClick = (slot: { start: number; end: number }) => {
    handleTimeSlotClick(slot)
    onTimeSlotSelect(
      selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end
        ? null
        : slot
    )
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction * 50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction * -50,
      opacity: 0
    })
  }

  const rawSlots = selectedDate ? getAvailableTimeSlots(selectedDate) : []
  const availableSlots = get30MinuteIntervals(rawSlots)
  
  // Determine grid columns based on number of available slots
  // 1-4 slots: 2 columns, 5+ slots: 4 columns
  const gridColsClass = availableSlots.length <= 4 ? 'grid-cols-2' : 'grid-cols-4'

  return (
    <motion.div
      className="bg-card rounded-lg p-4 w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar Section */}
        <div className="flex flex-col">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-3">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrevMonth}
              disabled={isLoading}
              className="p-1 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </motion.button>

            <motion.h2
              key={currentMonth.toISOString()}
              custom={slideDirection}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="text-base font-semibold text-foreground"
            >
              {format(currentMonth, 'MMMM yyyy')}
            </motion.h2>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNextMonth}
              disabled={isLoading}
              className="p-1 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <ChevronRight className="w-5 h-5 text-foreground" />
            </motion.button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1.5">
            {/* Day names */}
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                {day}
              </div>
            ))}

            {/* Padding days */}
            {paddingDays.map((_, i) => (
              <div key={`pad-${i}`} />
            ))}

            {/* Actual days */}
            {daysInMonth.map(date => {
              const isSelected = selectedDate && isSameDay(date, selectedDate)
              const isPast = isBefore(date, startOfToday())
              const isBeforeEarliest = isBefore(date, earliestDate)
              const hasSlots = hasAvailableSlots(date)
              const isDisabled = isPast || isBeforeEarliest || !hasSlots

              return (
                <motion.button
                  key={date.toISOString()}
                  whileHover={{ scale: isDisabled ? 1 : 0.95 }}
                  whileTap={{ scale: isDisabled ? 1 : 0.9 }}
                  onClick={() => !isDisabled && handleDateClick(date)}
                  disabled={isDisabled || isLoading}
                  type="button"
                  className={cn(
                    "aspect-square rounded-md flex items-center justify-center text-sm transition-all font-medium",
                    isSelected && "bg-primary text-primary-foreground shadow-md",
                    !isSelected && !isDisabled && "hover:bg-muted border border-transparent hover:border-primary/20",
                    isDisabled && "opacity-30 cursor-not-allowed",
                    !isSelected && !isDisabled && "text-foreground",
                    !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-40"
                  )}
                >
                  {format(date, 'd')}
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* Time Slots Section - Right column */}
        <div className="flex flex-col overflow-hidden">
          {selectedDate ? (
            <motion.div
              className="flex flex-col flex-1 overflow-hidden"
              key={selectedDate.toISOString()}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="font-semibold text-sm text-foreground mb-2 flex-shrink-0">
                Available times for{' '}
                <motion.span
                  key={selectedDate.toISOString()}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.15 }}
                >
                  {format(selectedDate, 'MMM d')}
                </motion.span>
              </h3>

              {availableSlots.length > 0 ? (
                <div className={`grid ${gridColsClass} gap-2 w-full flex-1 auto-rows-max content-start`}>
                  {availableSlots.map((slot) => {
                    const isSelected = selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end

                    return (
                      <motion.button
                        key={`${slot.start}-${slot.end}`}
                        onClick={() => handleSlotClick(slot)}
                        disabled={isLoading}
                        className={cn(
                          "flex items-center justify-center px-3 py-2.5 rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted hover:bg-muted/80 text-foreground"
                        )}
                        type="button"
                      >
                        {formatTime(slot.start)}
                      </motion.button>
                    )
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-xs text-muted-foreground">
                  No available times on this date
                </div>
              )}
            </motion.div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[250px] text-sm text-muted-foreground">
              Select a date to view available times
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
